import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { requireAdmin } from "@/lib/admin-guard";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { DECKS, type DeckId } from "@/data/recovery-board-cards";
import type { BoardCardRow } from "@/data/board-card-row";

export const Route = createFileRoute("/admin_/board-cards")({
  ssr: false,
  beforeLoad: requireAdmin,
  component: BoardCardsAdmin,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Recovery Board Cards — Admin | Recovery Trauma™" },
      { name: "description", content: "Edit the Recovery Board card texts." },
    ],
  }),
});

const DECK_IDS: DeckId[] = ["reflection", "surprise", "grounding", "mystery"];

function BoardCardsAdmin() {
  const [deck, setDeck] = useState<DeckId>("reflection");
  const [rows, setRows] = useState<BoardCardRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("board_cards").select("*").order("sort_order");
    if (error) toast.error("Could not load cards");
    setRows((data ?? []) as BoardCardRow[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (r: BoardCardRow) => {
    if (!r.text.trim()) return toast.error("Card text can't be empty");
    const { error } = await supabase.from("board_cards").update({
      text: r.text.trim(), active: r.active, reward: r.reward,
      steps: r.steps?.filter((s) => s.trim()) ?? null,
      link_to: r.link_to?.trim() || null, link_label: r.link_label?.trim() || null,
    }).eq("id", r.id);
    error ? toast.error("Could not save — check the link starts with /") : toast.success("Card saved");
  };
  const add = async () => {
    const max = Math.max(-1, ...rows.filter((r) => r.deck === deck).map((r) => r.sort_order));
    const { error } = await supabase.from("board_cards").insert({
      deck, sort_order: max + 1, text: "New card — write your text here",
      reward: deck === "surprise" ? 2 : deck === "grounding" ? 1 : null,
    });
    error ? toast.error("Could not add card") : load();
  };
  const remove = async (id: string) => {
    if (!confirm("Delete this card?")) return;
    const { error } = await supabase.from("board_cards").delete().eq("id", id);
    error ? toast.error("Could not delete") : setRows((p) => p.filter((r) => r.id !== id));
  };
  const patch = (id: string, v: Partial<BoardCardRow>) =>
    setRows((p) => p.map((r) => (r.id === id ? { ...r, ...v } : r)));

  const list = rows.filter((r) => r.deck === deck);

  return (
    <main className="mx-auto max-w-3xl px-4 py-8">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" /> Admin</Link>
      <h1 className="mt-2 font-serif text-3xl">Recovery Board cards</h1>
      <p className="mt-1 text-muted-foreground">Edit what players read when they draw a card. Changes show on the board straight away.</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {DECK_IDS.map((d) => (
          <Button key={d} variant={d === deck ? "default" : "outline"} onClick={() => setDeck(d)}>
            {DECKS[d].emoji} {DECKS[d].name} ({rows.filter((r) => r.deck === d).length})
          </Button>
        ))}
      </div>

      <Button className="mt-4" onClick={add}><Plus className="mr-1 h-4 w-4" /> Add a card</Button>

      {loading ? <p className="mt-6">Loading…</p> : (
        <ul className="mt-6 space-y-4">
          {list.map((r, i) => (
            <li key={r.id} className="rounded-xl border bg-card p-4">
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="font-medium">Card {i + 1}</span>
                <label className="flex items-center gap-2">Show on board <Switch checked={r.active} onCheckedChange={(v) => patch(r.id, { active: v })} /></label>
              </div>
              <Textarea value={r.text} maxLength={600} onChange={(e) => patch(r.id, { text: e.target.value })} aria-label="Card text" />
              {deck === "grounding" && (
                <Textarea className="mt-2" placeholder="Steps — one per line" value={(r.steps ?? []).join("\n")}
                  onChange={(e) => patch(r.id, { steps: e.target.value.split("\n") })} aria-label="Steps" />
              )}
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                <Input placeholder="Link page, e.g. /journal" value={r.link_to ?? ""} onChange={(e) => patch(r.id, { link_to: e.target.value })} aria-label="Link page" />
                <Input placeholder="Link button text" value={r.link_label ?? ""} onChange={(e) => patch(r.id, { link_label: e.target.value })} aria-label="Link button text" />
                {deck !== "reflection" && (
                  <Input type="number" min={0} max={10} placeholder="Tokens" value={r.reward ?? ""}
                    onChange={(e) => patch(r.id, { reward: e.target.value === "" ? null : Number(e.target.value) })} aria-label="Recovery tokens" />
                )}
              </div>
              <div className="mt-3 flex justify-between">
                <Button variant="ghost" size="sm" onClick={() => remove(r.id)}><Trash2 className="mr-1 h-4 w-4" /> Delete</Button>
                <Button size="sm" onClick={() => save(r)}>Save</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
